import { makeStyles } from "tss-react/mui";

import { Theme } from "@mui/material";
import { Tooltip } from "@shared/Tooltip";

/**
 * The only three states a status cell can show:
 *
 * - `ok`: everything is fine, nothing to look at.
 * - `warning`: something is off and worth a look.
 * - `none`: nothing configured or nothing to check. Its cell never shows a number.
 */
export type Status = "ok" | "warning" | "none";

const statusIcons: Record<Status, string> = {
    ok: "fa-solid fa-circle-check",
    warning: "fa-solid fa-circle-exclamation",
    none: "fa-regular fa-circle",
};

const useStyles = makeStyles<{ status: Status }>()((theme: Theme, { status }) => ({
    tooltip: {
        display: "flex",
        justifyContent: "center",
        marginRight: theme.spacing(1),
    },
    icon: {
        color: {
            ok: theme.palette.emteria.success,
            warning: theme.palette.emteria.warning,
            none: theme.palette.emteria.text.secondary,
        }[status],
        display: "inline-block",
        verticalAlign: "middle",
    },
}));

/**
 * The icon of a status cell, as used by the groups and policies tables. Pair `ok` and `warning` with a percentage,
 * never `none`.
 */
export const StatusIcon = ({ status, tooltip }: { status: Status; tooltip: React.ReactNode }) => {
    const { classes } = useStyles({ status });

    return (
        <Tooltip title={tooltip} className={classes.tooltip}>
            <span className={classes.icon}>
                <i className={statusIcons[status]} />
            </span>
        </Tooltip>
    );
};

/**
 * `ok` when every counted device is fine, `warning` otherwise.
 */
export const getStatus = (part: number, total: number): Status => (part === total ? "ok" : "warning");
